import { FamilyTreeResponse } from '../types/familyTree';

const WIKIDATA_ENDPOINT = 'https://query.wikidata.org/sparql';
const WIKIDATA_API = 'https://www.wikidata.org/w/api.php';

async function getWikidataId(name: string): Promise<string | null> {
  const params = new URLSearchParams({
    action: 'wbsearchentities',
    search: name,
    language: 'en',
    format: 'json',
    origin: '*'
  });

  try {
    const response = await fetch(`${WIKIDATA_API}?${params}`);
    const data = await response.json();
    
    if (data.search && data.search.length > 0) {
      return data.search[0].id;
    }
    return null;
  } catch (error) {
    console.error('Error fetching Wikidata ID:', error);
    return null;
  }
}

async function getFamilyRelations(wikidataId: string): Promise<any> {
  const sparqlQuery = `
    SELECT ?relative ?relativeLabel ?relationType ?birthDate ?deathDate ?description WHERE {
      {
        wd:${wikidataId} wdt:P22 ?relative. # father
        BIND("father" AS ?relationType)
      } UNION {
        wd:${wikidataId} wdt:P25 ?relative. # mother
        BIND("mother" AS ?relationType)
      } UNION {
        wd:${wikidataId} wdt:P40 ?relative. # child
        BIND("child" AS ?relationType)
      } UNION {
        wd:${wikidataId} wdt:P26 ?relative. # spouse
        BIND("spouse" AS ?relationType)
      }
      OPTIONAL { ?relative wdt:P569 ?birthDate. }
      OPTIONAL { ?relative wdt:P570 ?deathDate. }
      OPTIONAL { ?relative schema:description ?description. FILTER(LANG(?description) = "en") }
      SERVICE wikibase:label { bd:serviceParam wikibase:language "[AUTO_LANGUAGE],en". }
    }
  `;

  const params = new URLSearchParams({
    query: sparqlQuery,
    format: 'json'
  });

  try {
    const response = await fetch(`${WIKIDATA_ENDPOINT}?${params}`, {
      headers: {
        'Accept': 'application/sparql-results+json'
      }
    });
    return await response.json();
  } catch (error) {
    console.error('Error fetching family relations:', error);
    throw error;
  }
}

function processWikidataResponse(data: any): FamilyTreeResponse {
  const processedData: any = {
    parents: [],
    children: [],
    spouses: []
  };

  data.results.bindings.forEach((result: any) => {
    const relative = {
      name: result.relativeLabel.value,
      years: result.birthDate && result.deathDate ? 
        `${new Date(result.birthDate.value).getFullYear()}-${new Date(result.deathDate.value).getFullYear()}` : 
        undefined,
      description: result.description?.value
    };

    switch (result.relationType.value) {
      case 'father':
      case 'mother':
        processedData.parents.push(relative);
        break;
      case 'child':
        processedData.children.push(relative);
        break;
      case 'spouse':
        processedData.spouses.push(relative);
        break;
    }
  });

  return processedData;
}

export const fetchFamilyTree = async (query: string): Promise<FamilyTreeResponse> => {
  try {
    const wikidataId = await getWikidataId(query);
    if (!wikidataId) {
      throw new Error('Person not found in Wikidata');
    }

    const familyData = await getFamilyRelations(wikidataId);
    return processWikidataResponse(familyData);
  } catch (err) {
    console.error('Error fetching family tree:', err);
    throw err instanceof Error ? err : new Error('Failed to load family tree. Please try again.');
  }
};